/** Parsed delivery lead time in whole weeks. */
export type LeadTimeWeeksRange = {
  min: number;
  max: number;
};

export type LeadTimeWeeksSource = {
  leadTimeWeeksMin?: number;
  leadTimeWeeksMax?: number;
  /** Early backfill used a single week value. */
  leadTimeWeeks?: number;
};

/** Parse staff-entered lead time (whole weeks). Returns undefined when empty. */
export function parseLeadTimeWeeksInput(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) return undefined;
  return n;
}

export function isValidLeadTimeWeeks(value: number | undefined): value is number {
  return value != null && Number.isFinite(value) && value >= 0 && Number.isInteger(value);
}

export function normalizeLeadTimeWeeksRange(
  min: number | undefined,
  max: number | undefined,
): LeadTimeWeeksRange | undefined {
  if (!isValidLeadTimeWeeks(min) && !isValidLeadTimeWeeks(max)) return undefined;
  const resolvedMin = isValidLeadTimeWeeks(min) ? min : max!;
  const resolvedMax = isValidLeadTimeWeeks(max) ? max : resolvedMin;
  if (resolvedMin > resolvedMax) return undefined;
  return { min: resolvedMin, max: resolvedMax };
}

/** Parse min/max inputs; returns undefined when both empty, null when invalid. */
export function parseLeadTimeWeeksRangeInput(
  minRaw: string,
  maxRaw: string,
): LeadTimeWeeksRange | undefined | null {
  const min = parseLeadTimeWeeksInput(minRaw);
  const max = parseLeadTimeWeeksInput(maxRaw);
  if (min === undefined && max === undefined) return undefined;
  const normalized = normalizeLeadTimeWeeksRange(min, max);
  if (!normalized) return null;
  return normalized;
}

export function submissionLeadTimeWeeksRange(source: LeadTimeWeeksSource): LeadTimeWeeksRange | undefined {
  const fromRange = normalizeLeadTimeWeeksRange(source.leadTimeWeeksMin, source.leadTimeWeeksMax);
  if (fromRange) return fromRange;
  if (isValidLeadTimeWeeks(source.leadTimeWeeks)) {
    return { min: source.leadTimeWeeks, max: source.leadTimeWeeks };
  }
  return undefined;
}

export function formatLeadTimeWeeksRange(range: LeadTimeWeeksRange | undefined): string | null {
  if (!range) return null;
  if (range.min === range.max) return `${range.min} wk`;
  return `${range.min}–${range.max} wk`;
}

export function formatSubmissionLeadTimeWeeks(source: LeadTimeWeeksSource): string | null {
  return formatLeadTimeWeeksRange(submissionLeadTimeWeeksRange(source));
}

/** @deprecated Use {@link formatLeadTimeWeeksRange}. */
export function formatLeadTimeWeeks(weeks: number | undefined): string | null {
  if (weeks == null || !Number.isFinite(weeks)) return null;
  return `${weeks} wk`;
}
